import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatDate } from '@/utils/datetime';
import type { PurgeInput, PurgePreview, PurgeRecord } from '@restaurant/api-contracts';
import {
  Block,
  BlockFooter,
  List,
  ListItem,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Popup,
  Preloader,
  Link as F7Link,
} from 'framework7-react';
import { useCallback, useEffect, useState } from 'react';

const PAGE_SIZE = 50;

interface Props {
  opened: boolean;
  onClose: () => void;
  input: PurgeInput | null;
  title: string;
}

export default function PurgePreviewPopup({ opened, onClose, input, title }: Props) {
  const t = useT().purge;
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

  const [records, setRecords] = useState<PurgeRecord[]>([]);
  const [count, setCount] = useState(0);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const loadPage = useCallback(
    async (from: string | undefined, append: boolean) => {
      if (!input || !restaurantId) return;
      setLoading(true);
      setFailed(false);
      try {
        const page = unwrapResult<PurgePreview>(
          await openapiClient.POST('/api/restaurants/{restaurantId}/purge/preview', {
            params: { path: { restaurantId } },
            body: { input, limit: PAGE_SIZE, cursor: from },
          }),
        );
        setCount(page.count);
        setCursor(page.nextCursor);
        setRecords((prev) => (append ? [...prev, ...page.records] : page.records));
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [input, restaurantId],
  );

  useEffect(() => {
    if (!opened) return;
    setRecords([]);
    setCursor(undefined);
    void loadPage(undefined, false);
  }, [opened, loadPage]);

  return (
    <Popup opened={opened} onPopupClosed={onClose}>
      <Page>
        <Navbar>
          <NavTitle>{title}</NavTitle>
          <NavRight>
            <F7Link popupClose>{t.close}</F7Link>
          </NavRight>
        </Navbar>

        {records.length > 0 && (
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
            {records.map((r) => (
              <ListItem
                key={r.id}
                title={r.name}
                after={formatDate(r.at)}
                subtitle={r.reference}
                text={r.status}
              />
            ))}
          </List>
        )}

        {loading && (
          <Block className="text-align-center">
            <Preloader />
          </Block>
        )}

        {!loading && failed && <BlockFooter>{t.couldntCount}</BlockFooter>}

        {!loading && !failed && records.length === 0 && (
          <BlockFooter>{t.nothingToShow}</BlockFooter>
        )}

        {!loading && cursor && (
          <Block className="text-align-center">
            <F7Link onClick={() => void loadPage(cursor, true)}>{t.loadMore}</F7Link>
          </Block>
        )}

        {!loading && records.length > 0 && (
          <BlockFooter>{t.showingOf(records.length, count)}</BlockFooter>
        )}
      </Page>
    </Popup>
  );
}
